import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Check, X, Trash2 } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";

const TABS = [
  { v: "pending", l: "Pending" },
  { v: "approved", l: "Approved" },
  { v: "rejected", l: "Rejected" },
  { v: "", l: "All" },
];

export default function AdminComments() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [tab, setTab] = useState(searchParams.get("status") || "pending");
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/comments", { params: tab ? { status: tab } : {} });
      setComments(data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [tab]);

  const approve = async (id) => {
    await api.post(`/admin/comments/${id}/approve`);
    toast.success("Approved");
    load();
  };
  const reject = async (id) => {
    await api.post(`/admin/comments/${id}/reject`);
    toast.success("Rejected");
    load();
  };
  const remove = async (id) => {
    if (!window.confirm("Delete this comment?")) return;
    await api.delete(`/admin/comments/${id}`);
    toast.success("Deleted");
    load();
  };

  return (
    <div data-testid="admin-comments-page">
      <h1 className="font-heading text-3xl font-medium text-slate-950 mb-2">Comments</h1>
      <p className="text-slate-500 mb-6">Approve, reject, or delete public comments.</p>

      <div className="flex gap-2 mb-6">
        {TABS.map((t) => (
          <button
            key={t.v || "all"}
            onClick={() => { setTab(t.v); setSearchParams(t.v ? { status: t.v } : {}); }}
            data-testid={`comments-tab-${t.v || "all"}`}
            className={`px-4 py-2 rounded-md text-sm font-medium ${
              tab === t.v ? "bg-slate-950 text-white" : "bg-white border border-slate-200 text-slate-700 hover:bg-slate-50"
            }`}
          >
            {t.l}
          </button>
        ))}
      </div>

      <div className="space-y-3">
        {loading ? (
          <div className="text-slate-400 text-sm">Loading…</div>
        ) : comments.length === 0 ? (
          <div className="text-slate-400 text-sm">No comments.</div>
        ) : comments.map((c) => (
          <div key={c.id} className="bg-white border border-slate-200 rounded-lg p-5" data-testid={`comment-row-${c.id}`}>
            <div className="flex items-start justify-between gap-4">
              <div className="flex-1">
                <div className="flex items-center gap-2 text-sm">
                  <span className="font-medium text-slate-950">{c.name}</span>
                  {c.email && <span className="text-slate-400 text-xs">{c.email}</span>}
                  <span className={`text-[10px] px-2 py-0.5 rounded uppercase tracking-wider ${
                    c.status === "approved" ? "bg-green-100 text-green-700" :
                    c.status === "rejected" ? "bg-rose-100 text-rose-700" :
                    "bg-amber-100 text-amber-700"
                  }`}>
                    {c.status}
                  </span>
                </div>
                <div className="text-xs text-slate-400 mt-1">
                  on <span className="text-slate-700">{c.project_title || "(deleted project)"}</span>
                  {" · "}{new Date(c.created_at).toLocaleString()}
                </div>
                <p className="text-slate-700 mt-3 leading-relaxed">{c.message}</p>
              </div>
              <div className="flex gap-1">
                {c.status !== "approved" && (
                  <button onClick={() => approve(c.id)} data-testid={`approve-${c.id}`} className="p-2 hover:bg-green-50 rounded text-green-600">
                    <Check className="w-4 h-4" />
                  </button>
                )}
                {c.status !== "rejected" && (
                  <button onClick={() => reject(c.id)} data-testid={`reject-${c.id}`} className="p-2 hover:bg-amber-50 rounded text-amber-600">
                    <X className="w-4 h-4" />
                  </button>
                )}
                <button onClick={() => remove(c.id)} data-testid={`delete-${c.id}`} className="p-2 hover:bg-rose-50 rounded text-rose-600">
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
